/**
 * GameController - Orchestrates game flow between stores
 *
 * This service manages the interaction between gameplay state, flow state,
 * and external services like data storage and daily challenges.
 */

import { ariadneSeat, ARIADNE_MODEL } from "../../ariadne/seat";
import { threadDefault } from "../../ariadne/thread";
import { bestRoutePath } from "../../utils/graphUtils";
import { pickUnplayedPair } from "../pairExclusion";
import { TUTORIAL_PAR } from "../tutorialGuidance";

import {
  getAllWordCollectionsWithStatus,
  allWordCollections,
} from "../../features/wordCollections";

import { unifiedDataStore } from "../../services/UnifiedDataStore";
import { dailyChallengesService } from "../../services/DailyChallengesService";
import { useGameplayStore } from "../stores/useGameplayStore";
import { useGameFlowStore } from "../stores/useGameFlowStore";
import { useAssetStore } from "../../stores/useAssetStore";
import { useDailyChallengeStore } from "../../stores/useDailyChallengeStore";
import { useModalStore } from "../../stores/useModalStore";
import { shouldGateRegularGames } from "../../config/ads";

import type { PersistentGameState } from "../../services/UnifiedDataStore";
import type { GameHistoryEntry } from "../../utils/gameHistory";
import { isCurrentGameValid } from "../../utils/gameStateValidation";

class GameController {
  /**
   * Initialize game data and restore any saved game
   */
  async loadInitialData(): Promise<boolean> {
    const gameFlowStore = useGameFlowStore.getState();
    const _gameplayStore = useGameplayStore.getState();

    try {
      console.log("🎮 GameController: Starting initialization...");
      gameFlowStore.setLoadingState(true);

      // PHASE 1: Load essential data first (fast startup)
      const loadEssentialPromise = useAssetStore.getState().loadEssentialData();
      const loadCurrentGamePromise =
        unifiedDataStore.loadCurrentGame("regular");
      const fetchDailyChallengePromise = useDailyChallengeStore
        .getState()
        .fetchDailyChallengeState();

      const [hasStoredGame, _, __] = await Promise.all([
        loadEssentialPromise,
        loadCurrentGamePromise,
        fetchDailyChallengePromise,
      ]);

      const savedGame = await unifiedDataStore.loadCurrentGame("regular");
      console.log("🎮 GameController: Loaded saved game:", !!savedGame);

      // The thread toggle's persisted default (spec D4).
      useGameplayStore
        .getState()
        .setPathDisplayMode({ ai: await threadDefault() });

      // PHASE 2: Load critical game data if needed
      let graphData = useAssetStore.getState().graphData;

      if (hasStoredGame || savedGame) {
        if (!graphData) {
          console.log("🎮 GameController: Loading graph data for saved game");
          await useAssetStore.getState().loadCriticalGameData();
          graphData = useAssetStore.getState().graphData;
        }
      }

      gameFlowStore.setLoadingState(false);
      gameFlowStore.setInitialDataLoaded(true);

      // Load word collections
      if (graphData) {
        const allCollectionsWithStatus = await getAllWordCollectionsWithStatus(
          allWordCollections,
          graphData,
        );

        const currentlyActiveCollections = allCollectionsWithStatus
          .filter((collection) => collection.isCurrentlyAvailable)
          .map(({ isCurrentlyAvailable, ...collection }) => collection);

        gameFlowStore.setWordCollections(
          allCollectionsWithStatus,
          currentlyActiveCollections,
        );

        // Restore the saved game only if it is still valid for the current graph.
        // A stale save (different or missing vocab_hash, or out-of-graph words) is
        // discarded so the player gets a fresh start instead of a broken board.
        if (savedGame && savedGame.gameStatus === "playing") {
          const currentVocabHash = useAssetStore.getState().vocabHash;
          if (isCurrentGameValid(savedGame, graphData, currentVocabHash)) {
            await this.restoreSavedGame(savedGame, graphData);
          } else {
            await unifiedDataStore.clearCurrentGame("regular");
          }
        }
      }

      console.log("🎮 GameController: Initialization completed successfully");
      return true;
    } catch (error) {
      console.error("🎮 GameController: Initialization error:", error);
      gameFlowStore.setLoadingState(
        false,
        error instanceof Error ? error.message : "Failed to load data",
      );
      return false;
    }
  }

  /**
   * Start a new random game. `skipDailyPriority` forces a genuinely random
   * game even on the first game of a session (used by the tutorial, which must
   * overlay a random game — never the daily).
   */
  async startRandomGame(
    opts: { skipDailyPriority?: boolean; isTutorial?: boolean } = {},
  ): Promise<void> {
    const gameplayStore = useGameplayStore.getState();
    // Prevent starting a new regular game if a regular game is already loading.
    // Allow switching from challenge/daily challenge to regular game.
    if (
      gameplayStore.gameStatus === "loading" ||
      (gameplayStore.gameStatus === "playing" &&
        !gameplayStore.isDailyChallenge &&
        !gameplayStore.isChallenge)
    ) {
      return;
    }

    let { graphData } = useAssetStore.getState();

    // Load graph data if needed
    if (!graphData) {
      console.log("🎮 GameController: Loading graph data for new game");
      await useAssetStore.getState().loadCriticalGameData();
      graphData = useAssetStore.getState().graphData;
      if (!graphData) {
        useGameFlowStore
          .getState()
          .setLoadingState(false, "Failed to load game data");
        return;
      }
    }

    // Read the daily store after the graph load: injecting the graph refreshes it.
    const { remainingFreeGames, hasPlayedToday, todaysChallenge } =
      useDailyChallengeStore.getState();

    // Check for daily challenge priority - only on fresh app launches,
    // not when user explicitly chooses to start a regular game. The finished
    // tutorial game does not count as the session's first game, so the New
    // Game after it starts today's daily. (A tutorial still in play returned above.)
    const isFirstGameOfSession =
      (gameplayStore.gameStatus === "idle" && !gameplayStore.currentWord) ||
      gameplayStore.isTutorial;
    const canPlayDailyChallenge = !hasPlayedToday && todaysChallenge;

    if (
      !opts.skipDailyPriority &&
      isFirstGameOfSession &&
      canPlayDailyChallenge
    ) {
      console.log("🎮 GameController: Prioritizing Daily Challenge");
      await this.startDailyChallengeGame();
      return;
    }

    // Check free games (with monetization kill switch)
    if (shouldGateRegularGames() && remainingFreeGames <= 0) {
      console.log(
        "🎮 GameController: No free games remaining (monetization enabled)",
      );
      // TODO: Implement ad-gated system
      useGameplayStore.getState().setGameState({ gameStatus: "idle" });
      useGameFlowStore.getState().setLoadingState(false);
      return;
    }

    console.log("🎮 GameController: Starting new random game");
    gameplayStore.setGameState({ gameStatus: "loading" });
    await unifiedDataStore.clearCurrentGame("regular");

    // Generate a word pair the player has never played (whole history, any
    // mode, the tutorial included) and that is not today's daily; falls back
    // to excluding only the daily if history leaves no valid pair.
    const todaysPair = dailyChallengesService.getTodaysChallenge();
    let history: GameHistoryEntry[] = [];
    try {
      history = await unifiedDataStore.getGameHistory();
    } catch {
      history = [];
    }
    // The tutorial asks for a par long enough for every tip to land (TUTORIAL_PAR).
    const pair = pickUnplayedPair(graphData, history, todaysPair, {
      par: opts.isTutorial ? TUTORIAL_PAR : undefined,
    });
    if (!pair.start || !pair.end) {
      gameplayStore.setGameState({
        gameStatus: "idle",
      });
      useGameFlowStore
        .getState()
        .setLoadingState(false, "Could not generate word pair");
      return;
    }

    // Set up game state. The gold path is fixed here, at game start.
    const goldPath = bestRoutePath(graphData, pair.start, pair.end);
    ariadneSeat.abandon();
    const gameState = {
      startWord: pair.start,
      targetWord: pair.end,
      currentWord: pair.start,
      playerPath: [pair.start],
      optimalPath: goldPath,
      suggestedPathFromCurrent: [...goldPath],
      gameStatus: "playing" as const,
      isChallenge: false,
      isDailyChallenge: false,
      isTutorial: !!opts.isTutorial,
      currentDailyChallengeId: undefined,
      aiPath: [],
      aiModel: null,
      ariadneStatus: null,
      gameReport: null,
      potentialRarestMovesThisGame: [],
    };

    gameplayStore.resetGameState(); // First reset to clean slate
    gameplayStore.setGameState(gameState); // Then set the new state

    // Save game state
    const persistentState: PersistentGameState = {
      ...gameState,
      pathDisplayMode: gameplayStore.pathDisplayMode,
      optimalChoices: [],
      backtrackHistory: [],
      startTime: Date.now(),
    };

    await unifiedDataStore.saveCurrentGame(persistentState, "regular");
    console.log("🎮 GameController: Random game started and saved");
  }

  /**
   * Start today's daily challenge
   */
  async startDailyChallengeGame(): Promise<void> {
    const { graphData } = useAssetStore.getState();
    const { todaysChallenge } = useDailyChallengeStore.getState();
    const gameplayStore = useGameplayStore.getState();

    if (!graphData || !todaysChallenge) {
      gameplayStore.setGameState({ gameStatus: "idle" });
      useGameFlowStore
        .getState()
        .setLoadingState(false, "Daily challenge not available");
      return;
    }

    console.log("🎮 GameController: Starting daily challenge game");

    // Check for existing game to backup
    const currentGame = await unifiedDataStore.loadCurrentGame("regular");
    if (currentGame && currentGame.gameStatus === "playing") {
      await unifiedDataStore.saveCurrentGame(currentGame, "temp");
      useGameFlowStore.getState().setShouldRestoreTempGame(true);
    }

    await unifiedDataStore.clearCurrentGame("regular");

    // The gold path is fixed here, at game start.
    const goldPath = bestRoutePath(
      graphData,
      todaysChallenge.startWord,
      todaysChallenge.targetWord,
    );
    ariadneSeat.abandon();

    const gameState = {
      startWord: todaysChallenge.startWord,
      targetWord: todaysChallenge.targetWord,
      currentWord: todaysChallenge.startWord,
      playerPath: [todaysChallenge.startWord],
      optimalPath: goldPath,
      suggestedPathFromCurrent: [...goldPath],
      aiPath: [],
      aiModel: null,
      ariadneStatus: null,
      gameStatus: "playing" as const,
      isChallenge: false,
      isDailyChallenge: true,
      isTutorial: false,
      currentDailyChallengeId: todaysChallenge.id,
      gameReport: null,
      potentialRarestMovesThisGame: [],
    };

    gameplayStore.resetGameState();
    gameplayStore.setGameState(gameState);

    const persistentState: PersistentGameState = {
      ...gameState,
      pathDisplayMode: gameplayStore.pathDisplayMode,
      optimalChoices: [],
      backtrackHistory: [],
      startTime: Date.now(),
    };

    await unifiedDataStore.saveCurrentGame(persistentState, "regular");
    console.log("🎮 GameController: Daily challenge game started");
  }

  /**
   * Test word collections for a specific date
   */
  async testWordCollectionsForDate(month: number, day: number): Promise<void> {
    const { graphData } = useAssetStore.getState();
    if (!graphData) return;

    try {
      const testDate = new Date(new Date().getFullYear(), month - 1, day);
      const allCollectionsWithStatus = await getAllWordCollectionsWithStatus(
        allWordCollections,
        graphData,
        testDate,
      );

      const currentlyActiveCollections = allCollectionsWithStatus
        .filter((collection) => collection.isCurrentlyAvailable)
        .map(({ isCurrentlyAvailable, ...collection }) => collection);

      useGameFlowStore
        .getState()
        .setWordCollections(
          allCollectionsWithStatus,
          currentlyActiveCollections,
        );
    } catch (error) {
      console.error("Error testing word collections:", error);
    }
  }

  /**
   * Reset all game state
   */
  reset(): void {
    console.log("🎮 GameController: Resetting all game state");

    useGameplayStore.getState().resetGameState();
    useGameFlowStore.getState().reset();
    useModalStore.getState().reset();
  }

  /**
   * Private: Restore a saved game
   */
  private async restoreSavedGame(
    savedGame: PersistentGameState,
    graphData: Record<string, { edges: Record<string, number> }>,
  ): Promise<void> {
    console.log("🎮 GameController: Restoring saved game");

    // The saved gold path is kept (it is fixed at game start); the Local
    // Optimal route is recomputed with the current engine.
    const localOptimalRoute = bestRoutePath(
      graphData,
      savedGame.currentWord || "",
      savedGame.targetWord || "",
    );

    // Her seat resumes from the save; a save from before she played is solo.
    const reveal = await ariadneSeat.resume(graphData, savedGame.match ?? null);

    const gameState = {
      startWord: savedGame.startWord,
      targetWord: savedGame.targetWord,
      currentWord: savedGame.currentWord,
      playerPath: savedGame.playerPath,
      optimalPath: savedGame.optimalPath,
      suggestedPathFromCurrent: localOptimalRoute,
      gameStatus: "playing" as const,
      // Without her seat there is no thread: a pre-release save's reference
      // route is not hers and is dropped.
      aiPath: reveal ? reveal.thread : [],
      aiModel: reveal ? ARIADNE_MODEL : null,
      ariadneStatus: reveal?.status ?? null,
      isChallenge: savedGame.isChallenge || false,
      isDailyChallenge: savedGame.isDailyChallenge || false,
      isTutorial: savedGame.isTutorial || false,
      currentDailyChallengeId: savedGame.currentDailyChallengeId,
      potentialRarestMovesThisGame:
        savedGame.potentialRarestMovesThisGame || [],
      // Checkpoints, backtracks and the full move history survive a resume
      // (Juggernaut, checkpoint reuse and move-judging achievements read them).
      optimalChoices: savedGame.optimalChoices || [],
      backtrackHistory: savedGame.backtrackHistory || [],
      moveHistory: savedGame.moveHistory || savedGame.optimalChoices || [],
      gameReport: null,
    };

    useGameplayStore.getState().resetGameState();
    useGameplayStore.getState().setGameState(gameState);

    // Restore path display mode
    if (savedGame.pathDisplayMode) {
      useGameplayStore.getState().setPathDisplayMode({
        ...savedGame.pathDisplayMode,
        suggested: false, // Always reset suggested to false
      });
    }
    useGameplayStore
      .getState()
      .setPathDisplayMode({ ai: await threadDefault() });

    console.log("🎮 GameController: Game state restored");
  }
}

// Export singleton instance
export const gameController = new GameController();
